/**
 * Token Refresh Utilities
 *
 * Provides automatic token refresh for API calls when the access_token expires.
 * See doc/client-side-token-refresh-guide.md for detailed documentation.
 *
 * Usage:
 * import { fetchWithTokenRefresh } from "api/withTokenRefresh";
 *
 * const response = await fetchWithTokenRefresh(url, {
 *   method: "POST",
 *   body: JSON.stringify(data),
 * });
 *
 * if (!response.ok) {
 *   throw new Error(`API error: ${response.status}`);
 * }
 *
 * The utility handles:
 * - Automatic retry with refreshed token on 401
 * - Redirect to login if session is invalid
 * - No need to check for 401 in calling code
 */
import _ from "lodash";

import qlog from "views_shared/app/javascript/scripts/qlog";
import { authenticatedHeadersHash, accessToken } from "views_shared/app/javascript/scripts/authentication";

// Mirrors api.js's resolveApiUrl (can't import it here — api.js imports fetchWithTokenRefresh from
// this file, and the reverse import would be a dependency cycle). V1-COMPAT: no-ops in V2
// (V1_HOSTED unset); when this file is copied verbatim into V1, the refresh POST below must cross
// origin to V2 like every other Rails API call.
const resolveApiUrl = (url) => {
  if (!globalThis.Qwoted?.V1_HOSTED) return url;
  if (/^https?:\/\//i.test(url)) return url;
  const v2Url = globalThis.Qwoted?.V2_URL;
  return v2Url ? `${v2Url}${url}` : url;
};

/**
 * Get CSRF token from Rails meta tag
 * @returns {string|null} The CSRF token or null if not found
 */
export const getCSRFToken = () => {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
};

/**
 * Refresh the access token by calling the Rails refresh endpoint. It only
 * allows one refresh request in flight.
 *
 * This uses the server-side refresh token (stored in Rails session)
 * to get a new access_token cookie. The refresh token is never exposed
 * to JavaScript for security reasons.
 *
 * @returns {Promise<boolean>} true if refresh succeeded, false if user needs to re-login
 */
let inFlightAccessTokenRefreshRequest = null;
export const refreshAccessToken = async () => {
  let requestOwner = false;
  try {
    if (!inFlightAccessTokenRefreshRequest) {
      requestOwner = true;
      inFlightAccessTokenRefreshRequest = fetch(resolveApiUrl("/account/session_api/refresh_tokens"), {
        method: "POST",
        headers: {
          "X-CSRF-Token": getCSRFToken(),
          "Content-Type": "application/json",
        },
        credentials: "same-origin", // Include cookies
      });
    }

    const response = await inFlightAccessTokenRefreshRequest;

    if (response.ok) {
      return true; // New access_token cookie has been set
    }

    if (response.status === 401) {
      // Session is invalid - user needs to re-login
      return false;
    }

    // Other errors (500, network, etc)
    qlog.error("Token refresh failed:", response.status, response.statusText);
    return false;
  } catch (error) {
    // Network errors, etc
    qlog.error("Token refresh error:", error);
    return false;
  } finally {
    if (requestOwner) {
      inFlightAccessTokenRefreshRequest = null;
    }
  }
};

/* High level fetchWithTokenRefresh helper functions */
const throwSessionExpiredError = () => {
  throw new Error("Session expired, redirecting to login");
};

const handleAuthFailure = (redirectOnAuthFailure, fallback) => {
  // V1-COMPAT: never hard-redirect a V1-hosted page from this layer. V1 owns its own auth
  // lifecycle and does not serve /account/sign_in, so a failed V2 session there must degrade
  // to error-shaped results for the caller instead of navigating the page away.
  if (redirectOnAuthFailure && !globalThis.Qwoted?.V1_HOSTED) {
    qlog.warn("Authentication failed, redirecting to login");
    window.location.href = "/account/sign_in";
    throwSessionExpiredError();
  }
  return fallback();
};

const doAuthenticatedFetch = (url, options) =>
  fetch(url, { ...options, headers: new Headers({ ...authenticatedHeadersHash(), ...options.headers }) });

/**
 * Wraps a fetch call with automatic token refresh on 401 Unauthorized.
 *
 * When the access_token expires (after 60 minutes), this function:
 * 1. Catches the 401 error
 * 2. Calls the refresh endpoint to get a new token
 * 3. Retries the request with the new token
 * 4. If refresh fails, redirects to login page
 *
 * This handles ALL auth failure cases in one place - calling code doesn't
 * need to check for 401 or handle redirects.
 *
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options (method, body, etc.)
 * @param {Object} config - Configuration options
 * @param {boolean} config.redirectOnAuthFailure - Whether to redirect to login on auth failure (default: true)
 * @returns {Promise<Response>} The fetch response
 *
 * @example
 * // Simple usage - handles everything automatically
 * const response = await fetchWithTokenRefresh(url, {
 *   method: "POST",
 *   body: JSON.stringify(data),
 * });
 *
 * // Just check for success and handle errors
 * if (!response.ok) {
 *   throw new Error(`API error: ${response.status}`);
 * }
 */
export const fetchWithTokenRefresh = async (rawUrl, options = {}, { redirectOnAuthFailure = true } = {}) => {
  // Resolve here, not only in api.js's verbs: call sites that build their own URL and call
  // this function directly must also cross origin to V2 when the page is V1-hosted.
  // Idempotent for already-resolved and absolute URLs.
  const url = resolveApiUrl(rawUrl);

  if (!accessToken()) {
    qlog.info("No access token on file (long expired?), attempting token refresh");
    const refreshed = await refreshAccessToken();
    if (!refreshed) return handleAuthFailure(redirectOnAuthFailure, throwSessionExpiredError);
  }

  const response = await doAuthenticatedFetch(url, options);
  if (response.status !== 401) return response;

  qlog.info("Received 401 from PostgREST, attempting token refresh");
  const refreshed = await refreshAccessToken();
  if (!refreshed) return handleAuthFailure(redirectOnAuthFailure, _.constant(response));

  qlog.info("Token refreshed, retrying request");
  const retryResponse = await doAuthenticatedFetch(url, options);
  if (retryResponse.status === 401) return handleAuthFailure(redirectOnAuthFailure, _.constant(retryResponse));

  return retryResponse;
};
